import type { RespondentProviderProfile, Service } from '@/api/orderDetails'

/**
 * One assigned provider on an order, merged across every service they cover.
 */
export interface OrderProviderSummary {
  /** Stable list key: the provider id when the API supplies one, else the normalized name. */
  key: string
  name: string
  phone: string | null
  organizationName: string | null
  averageRating: number | null
  totalReviews: number | null
  /** Backend-formatted fallback, e.g. "4.5/5 (23 reviews)". */
  ratingDisplay: string | null
  /** Names of the services this provider is assigned to, in service order. */
  serviceNames: string[]
}

const normalizeName = (name: string): string => name.trim().toLowerCase().replace(/\s+/g, ' ')

/** Decimal fields can arrive as strings over GraphQL; treat 0 and unparseable values as "no rating". */
export const parseProviderRating = (value: number | string | null | undefined): number | null => {
  if (value == null) return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Provider-side identity for a service, harvested from its survey respondents.
 *
 * Matching is scoped to a single service rather than done globally by id: `respondent.id`
 * is a user id while `assignedProviderId` is a provider-profile id, so they cannot be
 * compared directly. Within one service the provider respondent is unambiguous.
 */
function findServiceProviderProfile(service: Service): RespondentProviderProfile | null {
  const profiles = (service.clientSurveyResponses ?? [])
    .map((response) => response.respondent?.providerProfile)
    .filter((profile): profile is RespondentProviderProfile => profile != null)

  if (profiles.length === 0) return null

  const providerName = service.providerName?.trim()
  if (providerName) {
    const byName = profiles.find(
      (profile) => profile.fullName && normalizeName(profile.fullName) === normalizeName(providerName)
    )
    if (byName) return byName
  }

  // No name match: only trust the profile when the service has exactly one candidate.
  return profiles.length === 1 ? profiles[0] : null
}

/**
 * Collect the distinct providers assigned across an order's services, enriched with
 * organization and rating from the provider profile when the API exposes one.
 */
export function collectOrderProviders(services: Service[] | null | undefined): OrderProviderSummary[] {
  const byKey = new Map<string, OrderProviderSummary>()

  for (const service of services ?? []) {
    const profile = findServiceProviderProfile(service)
    const name = service.providerName?.trim() || profile?.fullName?.trim()
    if (!name) continue

    const key = service.assignedProviderId || normalizeName(name)
    const existing = byKey.get(key)

    if (!existing) {
      byKey.set(key, {
        key,
        name,
        phone: service.providerPhone?.trim() || null,
        organizationName: profile?.organizationName?.trim() || null,
        averageRating: parseProviderRating(profile?.averageRating),
        totalReviews: profile?.totalReviews ?? null,
        ratingDisplay: profile?.ratingDisplay?.trim() || null,
        serviceNames: service.serviceName ? [service.serviceName] : [],
      })
      continue
    }

    // Later services only fill gaps — the first assignment wins on conflicts.
    existing.phone ||= service.providerPhone?.trim() || null
    existing.organizationName ||= profile?.organizationName?.trim() || null
    existing.averageRating ??= parseProviderRating(profile?.averageRating)
    existing.totalReviews ??= profile?.totalReviews ?? null
    existing.ratingDisplay ||= profile?.ratingDisplay?.trim() || null
    if (service.serviceName && !existing.serviceNames.includes(service.serviceName)) {
      existing.serviceNames.push(service.serviceName)
    }
  }

  return [...byKey.values()]
}
